/**
 * Delivery Client Portal View Component (SO-P274, E4.b)
 * Subject renderer for DELIVERY_ENGAGEMENT per mockup del-portal.
 */

import React, { useState } from 'react'
import { Button } from '../Common/Button'
import { useNls } from '../../nls/nlsProvider'
import type {
  ClientPortalDeliveryApproval,
  ClientPortalDeliveryChangeRequest,
  ClientPortalDeliveryMilestone,
  ClientPortalDeliveryUatItem,
  ClientPortalDeliveryUatRound,
  ClientPortalDeliveryView,
  OpsClientLink,
  OpsUatItemResult,
  OutsideApprovalDecision,
} from '../../types/ops'
import {
  decideClientPortalApproval,
  setClientUatResult,
} from '../../services/opsClientLinksService'

function makePortalOpId(prefix: string): string {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`
}

export interface DeliveryClientPortalViewProps {
  token: string
  view: ClientPortalDeliveryView
  link: OpsClientLink | null
  onRefresh: () => Promise<void>
  onSignOut: () => Promise<void>
}

export function DeliveryClientPortalView({
  token,
  view,
  onRefresh,
  onSignOut,
}: DeliveryClientPortalViewProps): React.ReactElement {
  const { t } = useNls()

  const [activeApprovalId, setActiveApprovalId] = useState<string | null>(null)
  const [revisionComment, setRevisionComment] = useState<string>('')
  const [actionError, setActionError] = useState<string | null>(null)
  const [busyAction, setBusyAction] = useState<boolean>(false)

  // UAT item action notes
  const [uatNotes, setUatNotes] = useState<Record<string, string>>({})

  const initials = view.workspaceName
    .split(/\s+/)
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase()

  const handleApprovalDecision = async (
    approval: ClientPortalDeliveryApproval,
    decision: OutsideApprovalDecision,
  ) => {
    setActionError(null)
    if (decision === 'REVISION_REQUESTED') {
      const trimmed = revisionComment.trim()
      if (trimmed.length < 3 || trimmed.length > 500) {
        setActionError(t('NLSOpsClientPortalRevisionCommentPlaceholder'))
        return
      }
    }

    setBusyAction(true)
    try {
      const opId = makePortalOpId('portal-decide')
      await decideClientPortalApproval({
        token,
        approvalId: approval.approvalId,
        decision,
        comment: decision === 'REVISION_REQUESTED' ? revisionComment.trim() : undefined,
        opId,
      })
      setActiveApprovalId(null)
      setRevisionComment('')
      await onRefresh()
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err)
      setActionError(msg)
    } finally {
      setBusyAction(false)
    }
  }

  const handleSetUatResult = async (
    roundId: string,
    itemId: string,
    result: OpsUatItemResult,
  ) => {
    setActionError(null)
    setBusyAction(true)
    try {
      const opId = makePortalOpId('portal-uat')
      await setClientUatResult({
        token,
        roundId,
        itemId,
        result,
        note: uatNotes[itemId]?.trim() || undefined,
        opId,
      })
      await onRefresh()
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err)
      setActionError(msg)
    } finally {
      setBusyAction(false)
    }
  }

  return (
    <div className="clientPortal__content" data-testid="delivery-client-portal-view">
      {/* Header */}
      <div className="clientPortal__headerRow">
        <div className="clientPortal__headerLeft">
          <div className="clientPortal__avatar">{initials}</div>
          <div>
            <div className="clientPortal__headerTitle">
              {view.workspaceName} · {view.engagementName}
            </div>
          </div>
        </div>
        <div className="clientPortal__actions">
          <span className="opsDeliveryPill opsDeliveryPill--neutral">{view.clientName}</span>
          <button
            type="button"
            className="btn sm clientPortal__signOutBtn"
            onClick={onSignOut}
            data-testid="portal-signout-btn"
          >
            {t('NLSOpsDeliveryAllocationsEndAction')}
          </button>
        </div>
      </div>

      {actionError && (
        <div className="clientLink__errorBanner" role="alert">
          {actionError}
        </div>
      )}

      {/* 1. Milestones */}
      <h3 className="clientPortal__sectionTitle">{t('NLSOpsClientPortalSectionMilestones')}</h3>
      {view.milestones.length === 0 ? (
        <div className="clientPortal__empty">{t('NLSOpsClientPortalMilestonesEmpty')}</div>
      ) : (
        <div className="clientPortal__list" data-testid="portal-milestones-list">
          {view.milestones.map((m: ClientPortalDeliveryMilestone, idx: number) => {
            const isDone = m.status === 'COMPLETED'
            const isInUat = m.status === 'IN_PROGRESS'
            const pillClass = isDone
              ? 'opsDeliveryPill opsDeliveryPill--good'
              : isInUat
                ? 'opsDeliveryPill opsDeliveryPill--info'
                : 'opsDeliveryPill opsDeliveryPill--neutral'
            const label = isDone ? 'Done' : isInUat ? 'In UAT' : m.dueDate

            return (
              <div key={`${m.title}-${idx}`} className="clientPortal__item">
                <div className="clientPortal__itemGrow">
                  <div className="clientPortal__itemTitle">{m.title}</div>
                </div>
                <span className={pillClass}>{label}</span>
              </div>
            )
          })}
        </div>
      )}

      {/* 2. Waiting for you */}
      <h3 className="clientPortal__sectionTitle">{t('NLSOpsClientPortalTabWaiting')}</h3>
      {view.waitingForYou.length === 0 ? (
        <div className="clientPortal__empty" data-testid="waiting-empty">
          {t('NLSOpsClientPortalWaitingEmpty')}
        </div>
      ) : (
        <div className="clientPortal__list" data-testid="portal-waiting-list">
          {view.waitingForYou.map((appr: ClientPortalDeliveryApproval) => {
            const isReviewOpen = activeApprovalId === appr.approvalId
            const titleLabel = appr.numberOrLabel
              ? `${appr.numberOrLabel} · ${appr.title}`
              : appr.title
            const descLabel =
              appr.subjectType === 'CHANGE_REQUEST'
                ? `${appr.hours} h · ${appr.priceDisplay}`
                : `${appr.release || 'Release'} · ${appr.title}`

            return (
              <div key={appr.approvalId} className="clientPortal__itemWrapper">
                <div className="clientPortal__item">
                  <div className="clientPortal__itemGrow">
                    <div className="clientPortal__itemTitle">{titleLabel}</div>
                    <div className="clientPortal__itemDesc">{descLabel}</div>
                  </div>
                  <Button
                    variant="primary"
                    size="sm"
                    disabled={busyAction}
                    onClick={() =>
                      setActiveApprovalId(isReviewOpen ? null : appr.approvalId)
                    }
                    data-testid={`review-btn-${appr.approvalId}`}
                  >
                    {t('NLSOpsClientPortalReviewBtn')}
                  </Button>
                </div>

                {isReviewOpen && (
                  <div className="clientPortal__reviewBox" data-testid={`review-box-${appr.approvalId}`}>
                    <div className="clientPortal__disclaimer">
                      {t('NLSOpsClientPortalDisclaimer')}
                    </div>

                    <div className="clientLink__field">
                      <textarea
                        className="clientLink__input"
                        rows={3}
                        placeholder={t('NLSOpsClientPortalRevisionCommentPlaceholder')}
                        value={revisionComment}
                        onChange={(e) => setRevisionComment(e.target.value)}
                        data-testid="revision-comment-input"
                      />
                    </div>

                    <div className="clientLink__actions">
                      <Button
                        variant="primary"
                        size="sm"
                        disabled={busyAction}
                        onClick={() => handleApprovalDecision(appr, 'APPROVE')}
                        data-testid="portal-approve-btn"
                      >
                        {t('NLSOpsClientPortalApproveBtn')}
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        disabled={busyAction}
                        onClick={() => handleApprovalDecision(appr, 'REVISION_REQUESTED')}
                        data-testid="portal-request-changes-btn"
                      >
                        {t('NLSOpsClientPortalRequestChangesBtn')}
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        disabled={busyAction}
                        onClick={() => handleApprovalDecision(appr, 'REJECT')}
                        data-testid="portal-reject-btn"
                      >
                        {t('NLSOpsClientPortalRejectBtn')}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      {/* 3. Change requests */}
      <h3 className="clientPortal__sectionTitle">{t('NLSOpsClientPortalTabChanges')}</h3>
      {view.changeRequests.length === 0 ? (
        <div className="clientPortal__empty">{t('NLSOpsClientPortalChangesEmpty')}</div>
      ) : (
        <div className="clientPortal__list" data-testid="portal-cr-list">
          {view.changeRequests.map((cr: ClientPortalDeliveryChangeRequest, idx: number) => {
            const statusClass =
              cr.status === 'APPROVED'
                ? 'opsDeliveryPill opsDeliveryPill--good'
                : cr.status === 'REJECTED'
                  ? 'opsDeliveryPill opsDeliveryPill--bad'
                  : 'opsDeliveryPill opsDeliveryPill--warn'

            return (
              <div key={`${cr.number}-${idx}`} className="clientPortal__item">
                <div className="clientPortal__itemGrow">
                  <div className="clientPortal__itemTitle">
                    {cr.number} · {cr.title}
                  </div>
                  <div className="clientPortal__itemDesc">
                    {cr.hours} h · {cr.priceDisplay}
                  </div>
                  {cr.whatChanges && (
                    <div className="clientPortal__itemDesc">{cr.whatChanges}</div>
                  )}
                </div>
                <span className={statusClass}>{cr.status}</span>
              </div>
            )
          })}
        </div>
      )}

      {/* 4. UAT */}
      <h3 className="clientPortal__sectionTitle">{t('NLSOpsClientPortalTabUat')}</h3>
      {view.uat.length === 0 ? (
        <div className="clientPortal__empty">{t('NLSOpsClientPortalUatEmpty')}</div>
      ) : (
        <div className="clientPortal__list" data-testid="portal-uat-list">
          {view.uat.map((rnd: ClientPortalDeliveryUatRound, rndIdx: number) => (
            <div key={`${rnd.release}-${rnd.roundNumber}-${rndIdx}`} className="clientPortal__itemWrapper clientPortal__uatRound">
              <div className="clientPortal__item">
                <div className="clientPortal__itemGrow">
                  <div className="clientPortal__itemTitle">
                    {rnd.release} ({rnd.counts.passed} passed · {rnd.counts.failed} failed · {rnd.counts.blocked} blocked)
                  </div>
                </div>
                <span className="opsDeliveryPill opsDeliveryPill--neutral">{rnd.status}</span>
              </div>

              {/* Items for open rounds */}
              {rnd.items && rnd.items.length > 0 && (
                <div className="clientPortal__uatItemsList">
                  {rnd.items.map((it: ClientPortalDeliveryUatItem) => (
                    <div
                      key={it.id}
                      className="clientPortal__item clientPortal__uatItemRow"
                      data-testid={`uat-item-row-${it.id}`}
                    >
                      <div className="clientPortal__itemGrow">
                        <div className="clientPortal__itemTitle">{it.text}</div>
                        {it.note && <div className="clientPortal__itemDesc">{it.note}</div>}
                        <input
                          type="text"
                          className="clientLink__input clientPortal__uatNoteInput"
                          placeholder={t('NLSOpsClientPortalUatNotePlaceholder')}
                          value={uatNotes[it.id] ?? ''}
                          onChange={(e) =>
                            setUatNotes({ ...uatNotes, [it.id]: e.target.value })
                          }
                          data-testid={`uat-note-input-${it.id}`}
                        />
                      </div>
                      <div className="clientLink__actions">
                        <Button
                          variant="secondary"
                          size="sm"
                          disabled={busyAction}
                          onClick={() => handleSetUatResult(rnd.roundId, it.id, 'PASSED')}
                          data-testid={`uat-pass-btn-${it.id}`}
                        >
                          {t('NLSOpsClientPortalPassBtn')}
                        </Button>
                        <Button
                          variant="secondary"
                          size="sm"
                          disabled={busyAction}
                          onClick={() => handleSetUatResult(rnd.roundId, it.id, 'FAILED')}
                          data-testid={`uat-fail-btn-${it.id}`}
                        >
                          {t('NLSOpsClientPortalFailBtn')}
                        </Button>
                        <Button
                          variant="secondary"
                          size="sm"
                          disabled={busyAction}
                          onClick={() => handleSetUatResult(rnd.roundId, it.id, 'BLOCKED')}
                          data-testid={`uat-block-btn-${it.id}`}
                        >
                          {t('NLSOpsClientPortalBlockBtn')}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* 5. Weekly reports */}
      <h3 className="clientPortal__sectionTitle">{t('NLSOpsClientPortalTabReports')}</h3>
      <div className="clientPortal__empty" data-testid="portal-no-reports">
        {t('NLSOpsClientPortalNoReports')}
      </div>

      {/* Footer */}
      <div className="clientPortal__footerNotice" data-testid="portal-footer-notice">
        Runs on Timelining · no internal chat, costs, rates or shadow staff are ever in this page (E4)
      </div>
    </div>
  )
}
